<script>
    // Vue 3 component for displaying a Leaflet map with clustering functionality
    import { createApp, h } from 'vue';
    import { LMap, LTileLayer } from '@vue-leaflet/vue-leaflet';

    // Leaflet components
    import L from 'leaflet';
    import 'leaflet.markercluster';

    import mixin from './../mixin';
    import StoryItem from './StoryItem.vue';
    import constants from './../../constants.js';

    export default {
        components: {
            LMap,
            LTileLayer
        },
        mixins: [mixin],
        props: {
            items: {
                type: Array,
                required: true
            },
            minLabel: {
                type: String,
                default: constants.labels.story.minutes
            }
        },
        data() {
            return {
                zoom: constants.maps.zoomLevels.desktop,
                center: [constants.maps.coordinates.utrecht.lat, constants.maps.coordinates.utrecht.lng],
                markerClusterGroup: null,
                constants: constants,
            };
        },
        watch: {
            items: {
                handler() {
                    this.refreshMarkers();
                },
                deep: true
            }
        },
        // Lifecycle hooks to initialize the map, handle screen size changes and initialize clustering
        mounted() {
            this.updateMap();
            this.changeScreenSize = this.debounce(() => this.updateMap());
            this.waitForMap(0);
            window.addEventListener('resize', this.changeScreenSize);
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.changeScreenSize);
        },
        methods: {
            // Method to initialize the clustering call after the map has been initialized
            waitForMap(retries) {
                this.$nextTick(() => {
                    const map = this.$refs.map?.leafletObject;
                    if (map) {
                        map.invalidateSize();
                        map.panTo(this.center);
                        this.setupClustering();
                    } else if (retries < 20) {
                        setTimeout(() => this.waitForMap(retries + 1), 100);
                    } else {
                        console.warn('Map did not initialize in time.');
                    }
                });
            },
            // Method to update the map's zoom level and center based on the current screen size
            updateMap() {
                const width = window.innerWidth;
                const { mobile, tablet, desktop } = this.constants.maps.zoomLevels;
                if (width < this.constants.breakpoints.mobile) this.zoom = mobile;
                else if (width < this.constants.breakpoints.tablet) this.zoom = tablet;
                else this.zoom = desktop;

                const map = this.$refs.map?.leafletObject;
                if (map) {
                    map.setZoom(this.zoom);
                    map.panTo(this.center);
                }
            },
            // Method to check whether clusters are needed for this zoom level or not
            setupClustering() {
                const map = this.$refs.map?.leafletObject;
                if (!map) return;

                if (!this.markerClusterGroup) {
                    this.markerClusterGroup = L.markerClusterGroup({
                        disableClusteringAtZoom: this.constants.maps.zoomLevels.widescreen + 1,
                        spiderfyOnMaxZoom: true,
                        showCoverageOnHover: false
                    });
                }

                map.on('zoomend', () => this.refreshMarkers());
                this.refreshMarkers();
            },
            // Method to refresh and populate the markers for clusters or single data points
            // Because clustering is not native to vue3-leaflet, we use leaflet.markercluster directly with app mounting per marker
            refreshMarkers() {
                const map = this.$refs.map?.leafletObject;
                if (!map || !this.markerClusterGroup) return;

                this.markerClusterGroup.clearLayers();
                map.removeLayer(this.markerClusterGroup);

                this.items.forEach(item => {
                    const lat = parseFloat(item.coordinates?.latitude);
                    const lng = parseFloat(item.coordinates?.longitude);
                    if (!isNaN(lat) && !isNaN(lng)) {
                        const marker = L.marker([lat, lng]);

                        const popupContent = document.createElement('div');
                        const mountEl = document.createElement('div');
                        mountEl.classList.add('columns');
                        popupContent.appendChild(mountEl);

                        marker.on('popupopen', () => {
                            const app = createApp({
                                mounted() {
                                    setTimeout(() => {
                                        const popup = marker.getPopup();
                                        popup.update();
                                        popup._source?.openPopup();
                                    }, 100);
                                },
                                render: () => h(StoryItem, {
                                    item,
                                    mode: this.constants.images.modes.normal,
                                    minLabel: this.minLabel,
                                    popup: true
                                })
                            });

                            app.mount(mountEl);
                        });

                        marker.bindPopup(popupContent, {
                            maxWidth: 'auto',
                            autoPan: true,
                        });
                        this.markerClusterGroup.addLayer(marker);
                    }
                });

                if (!map.hasLayer(this.markerClusterGroup)) {
                    map.addLayer(this.markerClusterGroup);
                }
            }
        }
    };
</script>

<template>
    <div class="leaflet has-radius-medium is-clipped">
        <l-map ref="map"
               :zoom="zoom"
               :center="center">
            <l-tile-layer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                          layer-type="base"
                          name="OpenStreetMap" />
        </l-map>
    </div>
</template>
